import React, { useEffect, useState } from 'react';
import {
    ActionIcon,
    Button,
    Flex,
    Highlight,
    Loader,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { theme } from '../../../global/style/theme.tsx';
import { useTranslation } from 'react-i18next';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useGetHealthcareProfessionals } from '../../../global/entities/healthcareProfessional/queries/useGetHealthcareProfessionals.tsx';
import { HealthcareProfessional } from '../../../global/entities/healthcareProfessional/HealthcareProfessional.ts';
import { IconArrowRight, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useGetHealthcareProfessionalById } from '../../../global/entities/healthcareProfessional/queries/useGetHealthcareProfessionalById.tsx';
import { useElementSize } from '@mantine/hooks';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { modals } from '@mantine/modals';
import { useDeleteHcpMutations } from '../../../global/entities/healthcareProfessional/mutations/useDeleteHcpMutations.tsx';

const HcpTable: React.FC = inject()(
    observer(() => {
        const { t } = useTranslation();
        const { data, isLoading, refetch } = useGetHealthcareProfessionals();
        const { isLoading: isLoadingGetHealthcareProfessionalById } = useGetHealthcareProfessionalById();
        const { mutateAsync: deleteHcp } = useDeleteHcpMutations();

        const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData } = useTable();
        const [search, setSearch] = useState('');
        const navigate = useNavigate();
        const { ref, width } = useElementSize();

        useEffect(() => {
            setSortedData([]);

            if (data?.length) 
                setSortedData(data);
            
        }, [data]);

        const openModalDelete = (questionnaireId: string) =>
            modals.openConfirmModal({
                title: t('modal.deleteItemModal.title'),
                children: (
                    <Stack gap={'sm'}>
                        <Text>{t('modal.deleteItemModal.question')}</Text>
                        <Text fw={'bold'}> {t('modal.deleteItemModal.details')}</Text>
                    </Stack>
                ),
                labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onCancel: () => console.log('Suppression annulée'),
                onConfirm: () => {
                    onClickDelete(questionnaireId);
                },
                size: 'lg',
                radius: 'xs',
            });

        const onClickDelete = (questionnaireId: string) => {
            deleteHcp(questionnaireId)
                .then(() => {
                    refetch();
                })
                .catch((error) => {
                    console.error('Erreur lors de la suppression :', error);
                });
        };

        if (isLoading) 
            return <Loading visible={true} loadingMessage={'Loading...'} />;

        const handleHcp = (id?: number) => {
            navigate(`/adminDashboard/hcp/${id}`);
        };

        return (
            <>
                <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
                    <Button onClick={() => navigate('/adminDashboard/hcp')} variant="transparent">
                        <Title c="black" order={4}>
                            {t('navbar.hcp')}
                        </Title>
                    </Button>
                    <Button
                        onClick={() => navigate('/adminDashboard/hcp/0')}
                        leftSection={<IconPlus />}
                        styles={{ label: { align: 'center', justifyContent: 'center' } }}
                    >
                        {t('hcp.addNewHCP')}
                    </Button>
                </Flex>
                <Paper w="100%" ref={ref}>
                    <Stack gap={'md'} h={'min-content'}>
                        <TextInput
                            placeholder={t('global.search')}
                            rightSection={<IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            width={'100%'}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                        <ScrollArea w="100%">
                            <Table verticalSpacing="xs">
                                <Table.Thead>
                                    <Table.Tr>
                                        <Th
                                            sorted={sortBy === 'name'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('name', sortedData)}
                                        >
                                            {t('formGlobal.name')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'healthcareProfessionalType'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('healthcareProfessionalType', sortedData)}
                                        >
                                            {t('formHcp.hcpType')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'licenseNumber'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('licenseNumber', sortedData)}
                                        >
                                            {t('formGlobal.licenseNumber')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'email'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('email', sortedData)}
                                        >
                                            {t('formGlobal.email')}
                                        </Th>
                                        <Table.Th></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {sortedData?.map((professional: HealthcareProfessional, index: number) => {
                                        const isSearch =
                                            containsStringIgnoreCase(
                                                [
                                                    professional.name,
                                                    professional.healthcareProfessionalType,
                                                    professional.licenseNumber,
                                                    professional.email,
                                                ],
                                                search,
                                            ) || !search;
                                        return (
                                            <Table.Tr
                                                key={professional.id}
                                                id={professional.id?.toString()}
                                                hidden={!isSearch}
                                                style={{
                                                    background:
                                                        index % 2 === 1
                                                            ? undefined
                                                            : 'var(--Background-Gray-light, #E9ECEF)',
                                                    pointer: 'cursor',
                                                }}
                                            >
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                        maw={200}
                                                    >
                                                        {professional.name}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {professional.healthcareProfessionalType}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {professional.licenseNumber}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {professional.email}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex align={'center'}>
                                                        <ActionIcon
                                                            tabIndex={-1}
                                                            variant="subtle"
                                                            color="red"
                                                            onClick={() => openModalDelete(professional.id!.toString())}
                                                        >
                                                            <IconTrash size={18} />
                                                        </ActionIcon>
                                                        <ActionIcon variant="transparent">
                                                            {isLoadingGetHealthcareProfessionalById ? (
                                                                <Loader
                                                                    size="xs"
                                                                    color={theme.colors?.blue?.toString()}
                                                                />
                                                            ) : (
                                                                <IconArrowRight
                                                                    onClick={() => handleHcp(professional.id)}
                                                                    color={theme.colors?.blue?.toString()}
                                                                />
                                                            )}
                                                        </ActionIcon>
                                                    </Flex>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    </Stack>
                </Paper>
            </>
        );
    }),
);

const WrappedHcpTable: React.FC = () => {
    return (
        <TableContextProvider>
            <HcpTable />
        </TableContextProvider>
    );
};

export default WrappedHcpTable;
